import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Briefcase,
  ChevronDown,
  CheckCircle2,
  FileSearch,
  FileStack,
  HelpCircle,
  Mail,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Users,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";

const guideSections = [
  {
    title: "Recommended Recruiter Workflow",
    description:
      "Create the job first, then scan CVs from that job so every candidate result stays organized.",
    steps: [
      "Create or select a job",
      "Click AI Pool Matches on the Jobs page to check saved candidates before uploading more CVs",
      "Set Scoring Criteria in Job Details if the role has must-have skill thresholds",
      "Import candidates from the Candidates page as general applicants or applicants for a selected job",
      "Review the Newly imported box after import",
      "Click Scan newly imported to open Bulk Scan with those candidates preselected",
      "Review Current Top Matches and the AI-enriched candidates",
      "Shortlist candidates and add recruiter notes",
      "Open Rankings or Scan History when you want to continue reviewing later",
    ],
  },
  {
    title: "Understanding Match Scores",
    description:
      "RecruitGlance uses recruiter criteria, semantic similarity, technical skill coverage, taxonomy mapping, and keyword evidence.",
    steps: [
      "Recruiter Criteria Match applies must-have thresholds and preferred-skill bonuses",
      "Semantic similarity compares the full CV and job description meaning",
      "Skill coverage checks required technical skills",
      "Experience evidence checks whether skills appear in work or project context",
      "Skill taxonomy gives partial credit for related technologies",
      "Detected and unconfirmed signals explain what was found and what still needs recruiter review",
      "Language and education are shown as supporting evidence, not automatic hiring decisions",
    ],
  },
  {
    title: "Candidate Decision Emails",
    description:
      "Reject candidates first, then review eligible recipients before sending recruiter-owned rejection emails.",
    steps: [
      "Connect Gmail or Outlook with sending permission when the consent popup appears",
      "Configure the rejection email template in Profile > App Configuration",
      "Shortlist or reject candidates from Rankings or Scan History",
      "Use Email Rejected to open the recipient review popup",
      "Already emailed rejected candidates are grouped separately and excluded from the next send list",
      "Unlock or update a locked decision only when the recruiter intentionally changes the outcome",
    ],
  },
  {
    title: "Inbox to Shortlist",
    description:
      "Use this agentic workflow when you want RecruitGlance to help move recent email applicants into a scan-ready review flow.",
    steps: [
      "Select a job",
      "Choose Gmail or Outlook",
      "Confirm mailbox access consent",
      "Import only recent Inbox PDF attachments related to the selected job",
      "Review the staged candidates before saving anything",
      "Approve the relevant candidates and continue to Bulk Scan",
      "Reject unrelated staged files so they do not keep appearing in the same import flow",
    ],
  },
  {
    title: "How Email Import Filters CVs",
    description:
      "RecruitGlance checks only recent Inbox emails within the selected import window, then stages likely matching PDF CVs for recruiter approval.",
    steps: [
      "The recruiter selects a job before running email import",
      "RecruitGlance checks recent Inbox messages inside the selected time range, such as the last 7, 14, or 30 days",
      "Only emails with PDF attachments are considered",
      "The email subject, body preview, selected keyword, job title, and CV content are checked for relevance",
      "Likely matching CVs are staged for review, not saved automatically",
      "A CV may not be imported if the email does not mention the selected job, the PDF is not readable, the attachment is not a CV, the CV is a duplicate, or the email is outside the selected time range",
      "If a valid CV is missed, broaden the keyword or import window, or upload the CV manually",
    ],
  },
  {
    title: "When to Use Rescan Duplicates",
    description:
      "Use rescan only when you want to refresh a previous result for the same job and same CV.",
    steps: [
      "Use it after changing scoring logic",
      "Use it after updating a job description",
      "Use it if old duplicate results look outdated",
      "Avoid using it when the previous result is still valid",
    ],
  },
];

const navigationFlow = [
  { label: "Dashboard", icon: BookOpen, text: "Start here and review activity" },
  { label: "Jobs", icon: Briefcase, text: "Create roles and check AI Pool Matches" },
  { label: "Candidates", icon: Users, text: "Manage imported/scanned profiles" },
  { label: "Bulk Scan", icon: FileStack, text: "Rank selected applicants for one job" },
  { label: "Rankings", icon: CheckCircle2, text: "Review decisions across scans" },
  { label: "AI Talent Search", icon: Search, text: "Search your saved candidate pool" },
];

const featureCards = [
  {
    title: "Bulk Scan",
    icon: FileStack,
    body: "Select pending job applicants and optional general candidates, then rank them against the job description and saved recruiter scoring criteria.",
  },
  {
    title: "Scoring Criteria",
    icon: ShieldCheck,
    body: "Open Job Details to set must-have skills such as PHP: 80 or Linux: 20, plus preferred skills such as Laravel or Docker.",
  },
  {
    title: "AI Pool Matches",
    icon: Sparkles,
    body: "Use the Jobs page button to find existing candidates in your saved pool who may already fit a newly created role.",
  },
  {
    title: "Criteria Simulator",
    icon: SlidersHorizontal,
    body: "Open it from Jobs or Job Details to preview how score targets and must-have skills affect the candidate review pool.",
  },
  {
    title: "Single CV Scan",
    icon: FileSearch,
    body: "Free users can run a quick one-candidate check against a selected role. Paid users should use the job-based Bulk Scan workflow for better history and decisions.",
  },
  {
    title: "AI Talent Search",
    icon: Search,
    body: "Search existing candidate profiles using natural recruiter-style requirements.",
  },
  {
    title: "Gmail and Outlook Import",
    icon: Mail,
    body: "Import recent Inbox PDF attachments from Gmail or Outlook for a selected job, then approve staged candidates before they are saved.",
  },
  {
    title: "Inbox to Shortlist",
    icon: Sparkles,
    body: "Use the agent workflow to import job-related email applicants, stage them for approval, and continue into Bulk Scan from one guided screen.",
  },
  {
    title: "Scan History",
    icon: FileStack,
    body: "Open previous scan batches, review foldable candidate rows, export results, and continue shortlisting or rejection decisions later.",
  },
  {
    title: "Background Activity",
    icon: Search,
    body: "Track AI Talent Search indexing after candidates are ready for scan. This can continue in the background without blocking candidate review.",
  },
  {
    title: "Shortlist and Notes",
    icon: CheckCircle2,
    body: "Save promising candidates and keep recruiter notes attached to the result.",
  },
  {
    title: "Candidate Email Settings",
    icon: Mail,
    body: "Configure recruiter-controlled rejection email template text from Profile > App Configuration. Mailbox sending consent is requested when sending is used.",
  },
  {
    title: "Pilot Access",
    icon: ShieldCheck,
    body: "Pilot access currently lasts 2 days, includes 75 scans, and allows up to 5 email-import attempts for safe evaluation.",
  },
  {
    title: "Account and Plan",
    icon: ShieldCheck,
    body: "Review profile details, plan status, and available account actions from Profile.",
  },
];

const troubleshootingRows = [
  ["Scan stuck", "Refresh the page, check Background Activity, and contact support if the scan does not continue."],
  ["AI Pool Matches is empty", "Import candidates first, or lower the minimum score by refreshing from Job Details after your pool grows."],
  ["No candidate search results", "Scan or import candidates first, then search again."],
  ["Old duplicate score appears", "Use Rescan Duplicates to refresh the stored result."],
  ["Criteria changes not visible", "Run a new scan or rescan candidates because criteria are applied when scoring runs."],
  ["Email import returns no candidates", "Check the selected time range, job keyword, Inbox folder, PDF attachment type, duplicate status, and whether the CV text is readable."],
  ["Decision email cannot be sent", "Connect Gmail or Outlook with sending permission and add the rejection email template in Profile > App Configuration."],
  ["PDF text missing", "The CV may be image-based or unreadable by text extraction."],
];

const helpCategories = [
  { title: "Getting Started", icon: BookOpen, target: "quick-start", body: "Create your first job and understand the main workflow." },
  { title: "Candidate Import", icon: Users, target: "candidate-import", body: "Upload PDFs or import job applicants from email." },
  { title: "Bulk Scan", icon: FileStack, target: "bulk-scan", body: "Rank selected candidates against a selected job." },
  { title: "Rankings", icon: CheckCircle2, target: "rankings", body: "Review scores, shortlist, reject, compare, and export." },
  { title: "AI Talent Search", icon: Search, target: "ai-search", body: "Reuse candidates already saved in your pool." },
  { title: "Account Setup", icon: ShieldCheck, target: "account", body: "Review plan, templates, reports, and account settings." },
];

const nextActionCards = [
  {
    title: "I have a new role",
    body: "Create the job, add requirements, then import applicants for that specific role.",
    action: "Start in Jobs",
    target: "#quick-start",
  },
  {
    title: "I received CVs",
    body: "Import candidates for the selected job, review the imported list, then scan pending applicants.",
    action: "Import workflow",
    target: "#candidate-import",
  },
  {
    title: "I need decisions",
    body: "Open Rankings to compare candidates, shortlist or reject, add notes, and prepare recruiter emails.",
    action: "Decision workflow",
    target: "#rankings",
  },
];

function FlowCard({ item, isLast }) {
  const Icon = item.icon;

  return (
    <div className="flex min-w-[180px] flex-1 items-stretch">
      <div className="flex w-full flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
          <Icon size={18} />
        </div>
        <h3 className="text-sm font-semibold text-slate-950">{item.label}</h3>
        <p className="mt-1 text-sm text-slate-500">{item.text}</p>
      </div>
      {!isLast && (
        <div className="hidden w-8 items-center justify-center text-slate-300 xl:flex">
          <span className="h-px w-full bg-slate-300" />
        </div>
      )}
    </div>
  );
}

function WorkflowNode({ icon: Icon, title, body, tone = "slate" }) {
  const toneClasses = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    slate: "border-slate-200 bg-white text-slate-700",
  };

  return (
    <div className={`relative rounded-xl border p-4 shadow-sm ${toneClasses[tone]}`}>
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-white/80 text-slate-950 shadow-sm">
        <Icon size={19} />
      </div>
      <h3 className="text-sm font-bold text-slate-950">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-slate-600">{body}</p>
    </div>
  );
}

function AnimatedConnector({ className = "" }) {
  return (
    <div className={`hidden items-center lg:flex ${className}`} aria-hidden="true">
      <div className="h-px flex-1 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full w-1/2 animate-[workflowSlide_1.8s_ease-in-out_infinite] rounded-full bg-emerald-500" />
      </div>
      <div className="h-2 w-2 rotate-45 border-r-2 border-t-2 border-emerald-500" />
    </div>
  );
}

function HelpWorkflowDiagram() {
  const importSteps = [
    { icon: Briefcase, title: "1. Create job", body: "Add the role and requirements before importing applicants.", tone: "emerald" },
    { icon: Users, title: "2. Import candidates", body: "Upload PDFs or import job-specific email applicants.", tone: "emerald" },
    { icon: FileStack, title: "3. Run Bulk Scan", body: "Select pending applicants and rank them against the job.", tone: "emerald" },
  ];
  const intelligenceSteps = [
    { icon: SlidersHorizontal, title: "Scoring criteria", body: "Must-have and preferred skills shape the final score.", tone: "sky" },
    { icon: Search, title: "AI search preparation", body: "Background preparation makes candidates searchable later.", tone: "sky" },
    { icon: Sparkles, title: "AI enrichment", body: "Top candidates receive summary, gaps, evidence, and questions.", tone: "sky" },
  ];
  const decisionSteps = [
    { icon: CheckCircle2, title: "Rankings", body: "Review score breakdowns and compare candidates side by side.", tone: "amber" },
    { icon: ShieldCheck, title: "Shortlist", body: "Save recruiter notes and move strong candidates forward.", tone: "amber" },
    { icon: Mail, title: "Decision emails", body: "Review shortlisted or rejected recipients before preparing emails.", tone: "amber" },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <style>{`
        @keyframes workflowSlide {
          0% { transform: translateX(-120%); opacity: 0.25; }
          45% { opacity: 1; }
          100% { transform: translateX(220%); opacity: 0.25; }
        }
      `}</style>

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
            Product map
          </span>
          <h2 className="mt-3 text-xl font-semibold text-slate-950">
            How RecruitGlance moves a job from applicants to decisions
          </h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
            This visual shows the main recruiter workflow and the AI/background steps that happen around it.
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_44px_1fr_44px_0.8fr]">
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-emerald-700">Recruiter actions</p>
          <div className="grid gap-3">
            {importSteps.map((step) => (
              <WorkflowNode key={step.title} {...step} />
            ))}
          </div>
        </div>

        <AnimatedConnector />

        <div className="rounded-2xl border border-sky-100 bg-sky-50/50 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-sky-700">AI processing layer</p>
          <div className="grid gap-3">
            {intelligenceSteps.map((step) => (
              <WorkflowNode key={step.title} {...step} />
            ))}
          </div>
        </div>

        <AnimatedConnector />

        <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-amber-700">Recruiter decision</p>
          <div className="grid gap-3">
            {decisionSteps.map((step) => (
              <WorkflowNode key={step.title} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ScoringFlowDiagram() {
  const scoringSteps = [
    {
      title: "1. Read JD + CV",
      body: "Uses the job description, CV text, saved recruiter criteria, and extracted candidate signals.",
      tone: "emerald",
    },
    {
      title: "2. Semantic relevance",
      body: "Embedding similarity checks broad CV-to-job meaning when wording differs.",
      tone: "sky",
    },
    {
      title: "3. Skill + taxonomy evidence",
      body: "Detected skills and related concepts are compared with the JD and criteria.",
      tone: "emerald",
    },
    {
      title: "4. Experience proof",
      body: "Skills are stronger when they appear in work history or project context.",
      tone: "amber",
    },
    {
      title: "5. Recruiter criteria",
      body: "Must-have thresholds can limit the score. Preferred skills add support.",
      tone: "rose",
    },
    {
      title: "6. Final score",
      body: "The score is normalized. Unclear evidence can be flagged for recruiter review.",
      tone: "slate",
    },
  ];

  const toneClasses = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    slate: "border-slate-200 bg-slate-50 text-slate-900",
  };

  return (
    <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-950 p-4 text-white shadow-sm sm:p-5">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-200">Scoring engine flow</p>
          <h3 className="mt-2 text-lg font-semibold">How the match score is produced</h3>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-100">
            RecruitGlance does not rely on one keyword check. Each layer adds or limits evidence so recruiters can see why a candidate ranked where they did.
          </p>
        </div>
        <span className="w-fit rounded-full border border-emerald-600 bg-emerald-900 px-3 py-1 text-xs font-semibold text-emerald-100">
          Human decision support
        </span>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_28px_1fr_28px_1fr] xl:grid-cols-[1fr_28px_1fr_28px_1fr_28px_1fr_28px_1fr_28px_1fr]">
        {scoringSteps.map((step, index) => (
          <React.Fragment key={step.title}>
            <div className={`relative overflow-hidden rounded-xl border p-4 ${toneClasses[step.tone]}`}>
              <div className="absolute inset-x-0 top-0 h-1 overflow-hidden bg-white/40">
                <div
                  className="h-full w-1/2 animate-[workflowSlide_2.6s_ease-in-out_infinite] rounded-full bg-emerald-500"
                  style={{ animationDelay: `${index * 0.16}s` }}
                />
              </div>
              <p className="text-sm font-bold">{step.title}</p>
              <p className="mt-2 text-xs leading-5 opacity-80">{step.body}</p>
            </div>

            {index < scoringSteps.length - 1 && (
              <div className="hidden items-center justify-center lg:flex" aria-hidden="true">
                <div className="h-px w-full overflow-hidden rounded-full bg-emerald-700">
                  <div
                    className="h-full w-2/3 animate-[workflowSlide_2.6s_ease-in-out_infinite] rounded-full bg-emerald-300"
                    style={{ animationDelay: `${index * 0.16}s` }}
                  />
                </div>
                <div className="h-2 w-2 shrink-0 rotate-45 border-r-2 border-t-2 border-emerald-300" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-amber-300 bg-amber-100 px-4 py-3 text-sm font-medium leading-6 text-amber-950 shadow-sm">
        <span className="font-extrabold">Important:</span> AI enrichment explains selected candidates, but the recruiter remains responsible for reviewing evidence and making the final hiring decision.
      </div>
    </div>
  );
}

export default function Help() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [openGuide, setOpenGuide] = useState(guideSections[0]?.title || "");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const leftPanel = <RecruiterSidebar activeItem="help" user={user} />;

  const mainContent = (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-xl border border-emerald-900 bg-[#03140f] text-white shadow-sm">
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:p-8">
          <div>
            <span className="inline-flex rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-100">
              RecruitGlance help center
            </span>
            <h2 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight md:text-4xl">
              Find the right workflow fast.
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-emerald-50/75">
              Use this page as a product map: choose where you are in the hiring flow and jump to the right workflow.
            </p>

            <div className="mt-6 grid gap-3 md:grid-cols-3">
              {nextActionCards.map((card) => (
                <a
                  key={card.title}
                  href={card.target}
                  className="rounded-lg border border-white/10 bg-white/[0.06] p-4 transition hover:-translate-y-0.5 hover:bg-white/[0.1]"
                >
                  <p className="text-sm font-semibold text-white">{card.title}</p>
                  <p className="mt-2 text-xs leading-5 text-emerald-50/70">{card.body}</p>
                  <span className="mt-3 inline-flex rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-100">
                    {card.action}
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.06] p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-100">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <p className="text-sm font-semibold">Best first run</p>
                <p className="text-xs text-emerald-50/60">A clean pilot/demo workflow recruiters can understand quickly.</p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {[
                "Create one job with a clear description",
                "Import 10 to 25 candidates for that job",
                "Run Bulk Scan and review the top matches",
                "Shortlist, reject, compare, and add notes",
                "Open Rankings or Scan History when you want the full decision view",
              ].map((step, index) => (
                <div key={step} className="flex gap-2 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2 text-xs font-medium text-emerald-50/85">
                  <span className="text-emerald-200">{index + 1}</span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {helpCategories.map((category) => {
          const Icon = category.icon;

          return (
            <a
              key={category.title}
              href={`#${category.target}`}
              className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 transition group-hover:bg-emerald-100">
                  <Icon size={19} />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">{category.title}</h3>
                  <p className="mt-1 text-sm leading-5 text-slate-500">{category.body}</p>
                </div>
              </div>
            </a>
          );
        })}
      </section>

      <section id="quick-start" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <HelpCircle size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-950">How to Navigate RecruitGlance</h2>
            <p className="mt-1 text-sm text-slate-500">
              Follow this path when screening candidates for a role.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 xl:flex-row">
          {navigationFlow.map((item, index) => (
            <FlowCard
              key={item.label}
              item={item}
              isLast={index === navigationFlow.length - 1}
            />
          ))}
        </div>
      </section>

      <HelpWorkflowDiagram />

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Guided Workflows</h2>
            <p className="mt-1 text-sm text-slate-500">
              Open only the workflow you need right now.
            </p>
          </div>
        </div>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {guideSections.map((section) => (
          <article key={section.title} className="bg-white">
            <button
              type="button"
              onClick={() => setOpenGuide(openGuide === section.title ? "" : section.title)}
              className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left transition hover:bg-slate-50"
            >
              <span>
                <span className="block text-base font-semibold text-slate-950">{section.title}</span>
                <span className="mt-1 block text-sm leading-5 text-slate-500">{section.description}</span>
              </span>
              <ChevronDown
                size={18}
                className={`mt-1 shrink-0 text-slate-400 transition ${openGuide === section.title ? "rotate-180 text-emerald-700" : ""}`}
              />
            </button>
            {openGuide === section.title && (
              <ol className="space-y-2 px-4 pb-4">
                {section.steps.map((step, index) => (
                  <li key={step} className="flex gap-2 text-sm text-slate-700">
                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            )}
          </article>
        ))}
        </div>
      </section>

      <section id="ai-search" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              AI Pool Matches
            </span>
              <h2 className="mt-3 text-xl font-semibold text-slate-950">
              Reuse candidates you already have before sourcing more CVs
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              From the Jobs page, click AI Pool Matches on a job card. RecruitGlance checks your saved candidate pool,
              excludes candidates already linked or scanned for that job, and suggests reusable candidates using the
              same scoring logic as Bulk Scan.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          {[
            "Open Jobs",
            "Click AI Pool Matches",
            "Select rediscovered candidates",
            "Add and scan them for the job",
          ].map((step, index) => (
            <div key={step} className="rounded-lg border border-emerald-200 bg-white p-4">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-800">
                {index + 1}
              </span>
              <p className="mt-3 text-sm font-semibold leading-5 text-slate-800">{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="bulk-scan" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              Recruiter Criteria Match
            </span>
              <h2 className="mt-3 text-xl font-semibold text-slate-950">
              Tell RecruitGlance how this job should be measured
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Open Job Details and add must-have skills with minimum thresholds. Preferred skills can improve ranking,
              but they do not block candidates like must-have requirements.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {[
            "Must-have example: PHP: 80",
            "Lower threshold example: Linux: 20",
            "Preferred example: Laravel, Docker",
          ].map((step, index) => (
            <div key={step} className="rounded-lg border border-emerald-200 bg-white p-4">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-800">
                {index + 1}
              </span>
              <p className="mt-3 text-sm font-semibold leading-5 text-slate-800">{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="candidate-import" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              Candidate import workflow
            </span>
              <h2 className="mt-3 text-xl font-semibold text-slate-950">
              From imported CVs to a scan-ready shortlist
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              When candidates are uploaded for a selected job, RecruitGlance highlights them as Newly imported.
              Email import from Gmail or Outlook stages PDF CVs first, so the recruiter can approve relevant candidates before they are saved.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          {[
            "Select the job before importing applicants",
            "Import via Direct Upload, Gmail, Outlook, or Inbox to Shortlist",
            "Approve staged email candidates before saving",
            "Open Bulk Scan with approved/imported candidates preselected",
          ].map((step, index) => (
            <div key={step} className="rounded-lg border border-emerald-200 bg-white p-4">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-800">
                {index + 1}
              </span>
              <p className="mt-3 text-sm font-semibold leading-5 text-slate-800">{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="rankings" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              Four-layer scoring
            </span>
              <h2 className="mt-3 text-xl font-semibold text-slate-950">
              RecruitGlance looks for meaning, skills, and hands-on evidence
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              The score combines semantic relevance, skill and taxonomy evidence, work-context proof, and recruiter criteria.
              Must-have thresholds can limit the score, while detected and unconfirmed signals help recruiters see what was found and what still needs review.
            </p>
          </div>
        </div>

        <ScoringFlowDiagram />
      </section>

      <section id="account" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <Mail size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Shortlisting, Rejection, and Email Templates</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              RecruitGlance helps organize recruiter decisions, but the recruiter controls candidate communication.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">Configure rejection template</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Open Profile, then App Configuration, to adjust the rejection email template before preparing candidate emails.
            </p>
          </article>
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">Review recipients</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Use Email Rejected from Rankings or Scan History after candidates are marked as rejected.
            </p>
          </article>
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">Avoid duplicate emails</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Already emailed rejected candidates are grouped separately and excluded unless the recruiter intentionally unlocks or changes them.
            </p>
          </article>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <FileStack size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Scan History and Background Activity</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              RecruitGlance keeps scan results reusable and separates long-running preparation from recruiter review.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">Scan History</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Review previous scan batches by job, open the same result view, expand candidate rows, and export results when needed.
            </p>
          </article>
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">Background Activity</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              AI Talent Search indexing can continue after candidates are ready for scan. Use this page to see recent indexing activity.
            </p>
          </article>
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">Bulk Scan recovery</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              If you leave Bulk Scan while a scan is running, return to the page to continue viewing the running state and latest result.
            </p>
          </article>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-xl font-semibold text-slate-950">Feature Reference</h2>
        <p className="mt-1 text-sm text-slate-500">
          Use this section when you are unsure which tool to open.
        </p>

        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {featureCards.map((feature) => {
            const Icon = feature.icon;

            return (
              <article key={feature.title} className="rounded-lg border border-slate-200 p-4">
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                    <Icon size={18} />
                  </span>
                  <h3 className="font-semibold text-slate-950">{feature.title}</h3>
                </div>
                <p className="text-sm leading-6 text-slate-600">{feature.body}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <SlidersHorizontal size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Technical Documentation: Criteria Simulator</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              The simulator helps recruiters test hiring strictness before rescanning or changing decisions.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">What it uses</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              It reads existing job details, candidate scores, matching skills, missing skills, and saved scoring breakdowns.
            </p>
          </article>
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">What it calculates</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              It shows which candidates pass the selected score target, which skills block candidates, and the biggest bottleneck.
            </p>
          </article>
          <article className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">What it does not do</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              It does not call AI, rescan CVs, change rankings, update shortlist status, or spend scan quota.
            </p>
          </article>
        </div>

        <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <h3 className="text-sm font-semibold text-emerald-950">Recommended use</h3>
          <p className="mt-2 text-sm leading-6 text-emerald-800">
            Open the simulator after a job has scan results. Adjust the minimum score and must-have skills to understand whether
            the role requirements are realistic, too strict, or producing a healthy shortlist pool.
          </p>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-xl font-semibold text-slate-950">Troubleshooting</h2>
        <p className="mt-1 text-sm text-slate-500">
          For recruiter questions and product decisions, open the FAQ page from the sidebar.
        </p>
        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Problem</th>
                <th className="px-3 py-2 font-semibold">What to Try</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {troubleshootingRows.map(([problem, fix]) => (
                <tr key={problem}>
                  <td className="px-3 py-3 font-medium text-slate-800">{problem}</td>
                  <td className="px-3 py-3 text-slate-600">{fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-xl font-semibold text-slate-950">Quick Start Checklist</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            "Register and verify email",
            "Create a job",
            "Open Bulk Scan",
            "Select the job",
            "Select candidates",
            "Review Current Top Matches",
            "Shortlist candidates",
            "Add recruiter notes",
            "Open Rankings or Scan History",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <CheckCircle2 size={16} className="text-emerald-600" />
              {item}
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  return (
    <DashboardLayout
      user={user}
      eyebrow="Help center"
      title="Help and Documentation"
      subtitle="A self-service guide for using RecruitGlance without support."
      leftPanel={leftPanel}
      mainContent={mainContent}
    />
  );
}
